import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const MYSTERY_REWARD_VARIANT_ID = 56919054057638;
const MYSTERY_REWARD_THRESHOLD = 22000;
const INCLUDED_PROTECTION_VARIANT_ID = 57709089128614;
const PAID_PROTECTION_VARIANT_ID = 57545965469862;
const PROTECTION_THRESHOLD = 14000;
const CHAIN_BUNDLE_HANDLES = new Set([
  '3-piece-jesus-peace', 'mona-lisa-jesus-chain-bundle', 'lucky-guava-bundle',
  'green-glacier-bundle', 'pink-lemonade-blueberry-bundle', 'lucky-charm-glacier-berry-bundle',
]);
const REWARD_SOURCE = 'fnk-cart-rewards';
const CART_ROOT = window.Shopify?.routes?.root || '/';

let syncPromise = null;
let syncQueued = false;
let bundleCheckoutPending = false;

function setExpressCheckoutPending(pending) {
  document.querySelectorAll('[data-fnk-cart-express-checkout]').forEach((container) => {
    container.inert = pending;
  });
}

function getSectionIds() {
  return [
    ...new Set(
      Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((component) => component.dataset.sectionId)
        .filter(Boolean)
    ),
  ];
}

async function getCart() {
  const response = await fetch(`${CART_ROOT}cart.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) throw new Error(`Unable to read cart (${response.status})`);
  return response.json();
}

async function mutateCart(path, payload) {
  const sectionIds = getSectionIds();

  if (sectionIds.length > 0) {
    payload.sections = sectionIds.join(',');
    payload.sections_url = `${window.location.pathname}${window.location.search}`;
  }

  const response = await fetch(`${CART_ROOT}${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(payload),
  });

  const result = await response.json();
  if (!response.ok || result.status) {
    throw new Error(result.description || result.message || `Unable to update cart (${response.status})`);
  }

  const updatedCart = await getCart();
  document.dispatchEvent(
    new CartUpdateEvent(updatedCart, REWARD_SOURCE, {
      source: REWARD_SOURCE,
      itemCount: updatedCart.item_count,
      sections: result.sections,
    })
  );
  return updatedCart;
}

function protectionIsIncluded(cart) {
  const hasChainBundle = cart.items.some((item) =>
    CHAIN_BUNDLE_HANDLES.has(item.handle) || item.properties?._fnk_chain_bundle === 'true'
  );
  const serviceTotal = cart.items.reduce((total, item) => {
    const isService = [PAID_PROTECTION_VARIANT_ID, INCLUDED_PROTECTION_VARIANT_ID].includes(Number(item.variant_id))
      || ['Shipping Protection', 'Shipping service'].includes(item.product_type)
      || item.gift_card;
    return total + (isService ? item.final_line_price : 0);
  }, 0);
  return hasChainBundle || cart.total_price - serviceTotal >= PROTECTION_THRESHOLD;
}

async function reconcileRewards() {
  let cart = await getCart();
  // Re-read after each mutation: discounts and bundle totals can change during a cart update.
  for (let pass = 0; pass < 6; pass++) {
    const includeProtection = protectionIsIncluded(cart);
    if (includeProtection && cart.items.some((item) =>
      Number(item.variant_id) === INCLUDED_PROTECTION_VARIANT_ID && item.final_line_price !== 0
    )) {
      throw new Error('Included shipping protection must be free. Please try checkout again.');
    }
    const updates = {};
    const additions = [];
    const rewards = [
      [INCLUDED_PROTECTION_VARIANT_ID, includeProtection, 'Shipping Protection'],
      [MYSTERY_REWARD_VARIANT_ID, cart.total_price >= MYSTERY_REWARD_THRESHOLD, 'Mystery Item'],
    ];
    for (const [variantId, unlocked, title] of rewards) {
      const items = cart.items.filter((item) => Number(item.variant_id) === variantId);
      if (unlocked && !items.length) additions.push({ id: variantId, quantity: 1, properties: { _fnk_reward: title } });
      items.forEach((item, index) => {
        const quantity = unlocked && index === 0 ? 1 : 0;
        if (item.quantity !== quantity) updates[item.key] = quantity;
      });
    }
    if (includeProtection) {
      cart.items.filter((item) => Number(item.variant_id) === PAID_PROTECTION_VARIANT_ID)
        .forEach((item) => { updates[item.key] = 0; });
    }
    if (Object.keys(updates).length) {
      cart = await mutateCart('cart/update.js', { updates });
    } else if (additions.length) {
      cart = await mutateCart('cart/add.js', { items: additions });
    } else {
      return;
    }
  }
  throw new Error('Cart rewards could not settle. Please try again.');
}

function syncRewards() {
  setExpressCheckoutPending(true);
  syncQueued = true;
  if (syncPromise) return syncPromise;
  syncPromise = (async () => {
    try {
      while (syncQueued) {
        syncQueued = false;
        await reconcileRewards();
      }
      setExpressCheckoutPending(false);
      return true;
    } catch (error) {
      console.error('[FNK cart rewards]', error);
      return false;
    } finally {
      syncPromise = null;
    }
  })();
  return syncPromise;
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.source !== REWARD_SOURCE) void syncRewards();
});

document.addEventListener(ThemeEvents.cartError, () => void syncRewards());
document.addEventListener('submit', (event) => {
  if (event.target instanceof HTMLFormElement && event.target.dataset.type === 'add-to-cart-form') {
    setExpressCheckoutPending(true);
  }
}, true);

// A product wallet creates a checkout independently of the storefront cart.
// Bundle shortcuts must add the bundle here, then settle the free benefit first.
async function checkoutBundle(button) {
  if (bundleCheckoutPending) return;
  const form = button.closest('form');
  if (!form || !form.reportValidity()) return;
  const formData = new FormData(form);
  const variantId = Number(formData.get('id'));
  const quantity = Number(formData.get('quantity') || 1);
  if (!Number.isSafeInteger(variantId) || variantId <= 0 || !Number.isInteger(quantity) || quantity <= 0) return;

  const status = button.parentElement.querySelector('[data-fnk-bundle-checkout-error]');
  bundleCheckoutPending = true;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  if (status) status.hidden = true;
  setExpressCheckoutPending(true);

  try {
    const properties = { _fnk_chain_bundle: 'true' };
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[([^\]]+)\]$/);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }
    const cart = await getCart();
    const items = [{ id: variantId, quantity, properties }];
    if (!cart.items.some((item) => Number(item.variant_id) === INCLUDED_PROTECTION_VARIANT_ID)) {
      items.push({ id: INCLUDED_PROTECTION_VARIANT_ID, quantity: 1, properties: { _fnk_reward: 'Shipping Protection' } });
    }
    await mutateCart('cart/add.js', { items });
    if (!await syncRewards()) throw new Error('Please try checkout again so we can confirm your included benefits.');
    window.location.assign(`${CART_ROOT}checkout`);
  } catch (error) {
    if (status) {
      status.hidden = false;
      status.textContent = error instanceof Error ? error.message : 'Unable to open checkout. Please try again.';
    }
  } finally {
    bundleCheckoutPending = false;
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

// Ensure the included benefit is settled before the standard cart checkout opens.
let checkoutReady = false;
document.addEventListener('click', async (event) => {
  const bundleButton = event.target instanceof Element ? event.target.closest('[data-fnk-bundle-checkout]') : null;
  if (bundleButton) {
    event.preventDefault();
    event.stopImmediatePropagation();
    await checkoutBundle(bundleButton);
    return;
  }
  const button = event.target instanceof Element ? event.target.closest('button[name="checkout"]') : null;
  if (!button || checkoutReady) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  button.setAttribute('aria-busy', 'true');
  const ready = await syncRewards();
  button.removeAttribute('aria-busy');
  if (ready) {
    checkoutReady = true;
    const currentButton = document.querySelector('button[name="checkout"]') || button;
    currentButton.click();
    checkoutReady = false;
  } else {
    const status = document.querySelector('[data-shipping-protection-status]');
    if (status) {
      status.classList.remove('visually-hidden');
      status.textContent = 'Please try checkout again so we can confirm your included benefits.';
    }
  }
}, true);

// Cover keyboard/form checkout as well as clicks on the checkout button.
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (checkoutReady || !(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
  if (event.submitter && event.submitter.name !== 'checkout') return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (await syncRewards()) {
    checkoutReady = true;
    try {
      // Section rendering can replace the form while benefits are settling.
      const currentButton = document.querySelector('button[name="checkout"]');
      if (currentButton) currentButton.click();
      else form.requestSubmit(event.submitter || undefined);
    } finally {
      checkoutReady = false;
    }
  } else {
    const status = document.querySelector('[data-shipping-protection-status]');
    if (status) {
      status.classList.remove('visually-hidden');
      status.textContent = 'Please try checkout again so we can confirm your included benefits.';
    }
  }
}, true);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void syncRewards(), { once: true });
} else {
  void syncRewards();
}
